package com.schoolconnect.app.repository.attendance

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.AttendanceUpdateRequest
import com.schoolconnect.app.datasource.network.request.AttendanceRecordRequest
import com.schoolconnect.app.datasource.network.response.StudentAttendanceResponse
import com.schoolconnect.app.datasource.network.response.QuickMarkDataResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive

object AttendanceListRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] AttendanceListRepository"
    private const val ATTENDANCE_RECORD_BATCH_SIZE = 100
    private const val ATTENDANCE_RECORD_MAX_PAGES = 100

    suspend fun loadQuickMarkData(classSectionId: String, targetDate: String): Result<QuickMarkDataResponse> {
        val normalizedClassSectionId = classSectionId.trim()
        val normalizedDate = targetDate.trim()
        if (normalizedClassSectionId.isBlank() || normalizedDate.isBlank()) {
            return Result.failure(IllegalArgumentException("Class and attendance date are required to load Quick Mark data."))
        }
        scLog("$TAG loadQuickMarkData start classSectionId=$normalizedClassSectionId attendanceDate=$normalizedDate")
        return KtorApiClient.getQuickMarkData(normalizedClassSectionId, normalizedDate)
            .onSuccess { data -> scLog("$TAG loadQuickMarkData success classSectionId=$normalizedClassSectionId attendanceDate=$normalizedDate students=${data.students?.size ?: 0}") }
            .onFailure { error -> scLog("$TAG loadQuickMarkData failure classSectionId=$normalizedClassSectionId attendanceDate=$normalizedDate", error) }
    }

    /**
     * Loads attendance records in bounded server pages and narrows them to the
     * visible roster locally. The old implementation issued one request per
     * student (and could repeat that whole wave after every refresh), creating
     * an avoidable N+1 request storm on the attendance screen.
     */
    suspend fun loadRecordsForStudents(
        studentIds: List<String>,
        classSectionId: String? = null,
        targetDate: String,
        status: String? = null
    ): Result<Map<String, StudentAttendanceResponse>> {
        val ids = studentIds.map(String::trim).filter(String::isNotBlank).distinct()
        val normalizedDate = targetDate.trim()
        if (normalizedDate.isBlank()) {
            return Result.failure(IllegalArgumentException("Attendance date is required."))
        }
        if (ids.isEmpty()) return Result.success(emptyMap())

        val normalizedClassSectionId = classSectionId?.trim()?.takeIf(String::isNotBlank)
        val normalizedStatus = status?.trim()?.takeIf(String::isNotBlank)
        return try {
            val records = buildMap {
                var offset = 0
                var pageCount = 0
                while (pageCount < ATTENDANCE_RECORD_MAX_PAGES) {
                    currentCoroutineContext().ensureActive()
                    val page = KtorApiClient.getAttendanceRecords(
                        classSectionId = normalizedClassSectionId,
                        targetDate = normalizedDate,
                        status = normalizedStatus,
                        limit = ATTENDANCE_RECORD_BATCH_SIZE,
                        offset = offset,
                        sortBy = "attendance_date",
                        order = "desc"
                    ).getOrThrow()
                    page.forEach { record ->
                        val studentId = record.studentId?.trim().orEmpty()
                        if (studentId in ids) put(studentId, record)
                    }
                    pageCount++
                    if (page.size < ATTENDANCE_RECORD_BATCH_SIZE) break
                    offset += ATTENDANCE_RECORD_BATCH_SIZE
                }
            }
            scLog("$TAG loadRecordsForStudents success targetDate=$normalizedDate students=${ids.size} records=${records.size}")
            Result.success(records)
        } catch (error: CancellationException) {
            throw error
        } catch (error: Exception) {
            scLog("$TAG loadRecordsForStudents failure targetDate=$normalizedDate students=${ids.size}", error)
            Result.failure(error)
        }
    }
    suspend fun updateAttendance(
        attendanceId: String,
        status: String,
        remarks: String?
    ): Result<StudentAttendanceResponse> {
        val normalizedId = attendanceId.trim()
        val normalizedStatus = status.trim()
        scLog("$TAG updateAttendance start attendanceId=$normalizedId status=$normalizedStatus")
        return KtorApiClient.updateAttendance(
            attendanceId = normalizedId,
            request = AttendanceUpdateRequest(
                status = normalizedStatus,
                remarks = remarks?.trim()?.takeIf { it.isNotBlank() }
            )
        ).onSuccess { scLog("$TAG updateAttendance success attendanceId=$normalizedId") }
            .onFailure { throwable -> scLog("$TAG updateAttendance failure attendanceId=$normalizedId", throwable) }
    }

    suspend fun createAttendance(
        studentId: String,
        classSectionId: String,
        targetDate: String,
        status: String,
        remarks: String?
    ): Result<StudentAttendanceResponse> {
        val normalizedStudentId = studentId.trim()
        val normalizedClassSectionId = classSectionId.trim()
        val normalizedDate = targetDate.trim()
        val normalizedStatus = status.trim()
        if (normalizedStudentId.isBlank() || normalizedClassSectionId.isBlank() || normalizedDate.isBlank() || normalizedStatus.isBlank()) {
            return Result.failure(IllegalArgumentException("Student, class, date, and attendance status are required."))
        }
        scLog(
            "$TAG createAttendance start studentId=$normalizedStudentId classSectionId=$normalizedClassSectionId " +
                "targetDate=$normalizedDate status=$normalizedStatus"
        )
        return KtorApiClient.createAttendance(
            targetDate = normalizedDate,
            request = AttendanceRecordRequest(
                studentId = normalizedStudentId,
                classSectionId = normalizedClassSectionId,
                status = normalizedStatus,
                remarks = remarks?.trim()?.takeIf { it.isNotBlank() }
            )
        ).onSuccess { scLog("$TAG createAttendance success studentId=$normalizedStudentId targetDate=$normalizedDate") }
            .onFailure { throwable -> scLog("$TAG createAttendance failure studentId=$normalizedStudentId targetDate=$normalizedDate", throwable) }
    }

    suspend fun findAttendanceRecord(studentId: String, targetDate: String): Result<StudentAttendanceResponse> {
        val normalizedStudentId = studentId.trim()
        val normalizedDate = targetDate.trim()
        if (normalizedStudentId.isBlank() || normalizedDate.isBlank()) {
            return Result.failure(IllegalArgumentException("Student and attendance date are required."))
        }
        scLog("$TAG findAttendanceRecord start studentId=$normalizedStudentId targetDate=$normalizedDate")
        return KtorApiClient.getAttendanceRecords(
            studentId = normalizedStudentId,
            targetDate = normalizedDate,
            limit = 100,
            offset = 0,
            sortBy = "attendance_date",
            order = "desc"
        ).fold(
            onSuccess = { rows ->
                rows.firstOrNull { it.studentId == normalizedStudentId && it.attendanceDate == normalizedDate }
                    ?.let { Result.success(it) }
                    ?: Result.failure(NoSuchElementException("No attendance record exists for the selected student and date."))
            },
            onFailure = { throwable -> Result.failure(throwable) }
        ).onFailure { throwable -> scLog("$TAG findAttendanceRecord failure studentId=$normalizedStudentId targetDate=$normalizedDate", throwable) }
    }

    suspend fun loadPage(
        classSectionId: String? = null,
        targetDate: String? = null,
        status: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("attendance_date", com.schoolconnect.app.feature.common.ServerSortOrder.DESC),
        forceRemote: Boolean = false
    ): Result<List<StudentAttendanceResponse>> {
        val safePageSize = pageSize.coerceIn(1, 100)
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val offset = safePageIndex * safePageSize
        scLog("$TAG loadPage start classSectionId=${classSectionId ?: "<any>"} targetDate=${targetDate ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote")
        return KtorApiClient.getAttendanceRecords(
            classSectionId = classSectionId,
            targetDate = targetDate,
            status = status,
            limit = safePageSize,
            offset = offset,
            sortBy = sort.normalizedField,
            order = sort.order.apiValue
        ).map { rows ->
            rows.withServerTotalCount(rows.serverTotalCountOrNull() ?: rows.size)
        }.onFailure { throwable -> scLog("$TAG loadPage failure pageIndex=$safePageIndex", throwable) }
    }
}
